import { Link } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { useSlugFlow } from "hooks/useSlugFlow";
import SoftInput from "components/SoftInput";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import { useMemo, useState, useEffect } from "react";
import defaultScheduleIcon from "assets/images/schedule.svg";
import { useAllowedPermissions } from "hooks/useAllowedPermissions";
import { PERMISSIONS } from "constants/portal";
import { useSchedules } from "hooks/useSchedules";
import SoftEditor from "components/SoftEditor";
import SchedulerDialog from "layouts/Flow Designer/components/ExecutionsCalendar/components/SchedulerDialog";
import { formatDateAndTime } from "helpers/common";
import { getLocalDateTimeString } from "helpers/common";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { useResourceDependencies } from "hooks/useResourceDependencies";

export const Schedules = () => {
   const { flow } = useSlugFlow();
   const [searchName, setSearchName] = useState("");
   const { isAllowedAction } = useAllowedPermissions();
   const { calendarEvents } = useSchedules();
   const [schedules, setSchedules] = useState([]);
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [showSchedulerDialog, setShowSchedulerDialog] = useState(false);
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(flow?.id);
   const { dependencies: flowScheduleIds } = useResourceDependencies({
      sourceId: flow?.id,
      direction: "reverse",
      targetResourceType: "schedule"
   });

   const handleOpenAddScheduleDialog = () => {
      setSelectedEvent(null);
      setShowSchedulerDialog(true);
   };

   const handleFilterSchedules = (schedule) => {
      const hasPermission = isAllowedAction(undefined, [PERMISSIONS.VIEW_ANY_SCHEDULES, PERMISSIONS.ACCESS_TO_SCHEDULER_PAGE]);

      return hasPermission && schedule.title?.toLowerCase()?.includes(searchName.toLowerCase());
   };

   const handleCloseDialog = () => {
      setSelectedEvent(null);
      setShowSchedulerDialog(false);
   };

   const tableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "icon",
               accessor: "icon",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value }) => <SoftBox width="60px">{value}</SoftBox>
            },
            {
               Header: "Name",
               accessor: "name",
               disableSortBy: false,
               Cell: ({ row, value }) => (
                  <SoftTypography fontSize="14px" fontWeight="bold">
                     <Link style={{ color: "#2D3748" }} className="white-to-dark" to={`/schedule/${row.original.id}`}>
                        {value}
                     </Link>
                  </SoftTypography>
               )
            },
            {
               Header: "Description",
               accessor: "description",
               disableSortBy: false,
               width: 380,
               Cell: ({ value }) => (
                  <SoftBox fontSize="12px">
                     <SoftEditor className="documentation preview schedule" theme="bubble" value={value} readOnly />
                  </SoftBox>
               )
            },
            {
               Header: "Status",
               accessor: "status",
               disableSortBy: false,
               Cell: ({ value }) => <StatusBadge status={value} />
            },
            {
               Header: "Last edited",
               accessor: "last_updated",
               disableSortBy: false,
               Cell: ({ value }) => <SoftTypography fontSize="12px">{value}</SoftTypography>
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: [
            ...schedules.filter(handleFilterSchedules).map((schedule) => {
               return {
                  id: schedule.id,
                  name: schedule.title,
                  description: schedule.description,
                  status: schedule.status?.toLowerCase(),
                  last_updated: schedule.updated_date_time ? formatDateAndTime(getLocalDateTimeString(schedule.updated_date_time)) : "",
                  icon: <SoftBox component="img" width="100%" src={defaultScheduleIcon} />
               };
            })
         ]
      };
   }, [schedules, flow, searchName]);

   useEffect(() => {
      setSchedules(calendarEvents.filter((event) => flowScheduleIds.includes(event.id)));
   }, [calendarEvents, flowScheduleIds]);

   return (
      <>
         <SoftBox>
            <SoftBox
               sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  justifyContent: "flex-start",
                  padding: "12px 0 12px 40px"
               }}
            >
               <SoftInput
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                     setSearchName(currentTarget.value);
                  }}
                  sx={{
                     maxWidth: "380px",
                     marginRight: "34px"
                  }}
               />
               {isAllowedAction(flow?.created_by_id, [PERMISSIONS.EDIT_WORKFLOWS, PERMISSIONS.EDIT_ANY_WORKFLOWS]) && !isReadOnlyAccess && (
                  <SoftButton variant="outlined" color="info" onClick={handleOpenAddScheduleDialog}>
                     add
                  </SoftButton>
               )}
            </SoftBox>
            <SoftBox>
               <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons tableView={"list"} tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
            </SoftBox>
         </SoftBox>
         <SchedulerDialog
            open={showSchedulerDialog}
            selectedEvent={selectedEvent}
            closeDialog={handleCloseDialog}
            flow={
               flow
                  ? {
                       value: flow.id,
                       name: flow.name
                    }
                  : undefined
            }
         />
      </>
   );
};
